import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react'

import { CopyIcon } from '@toasttab/buffet-pui-icons'
import { useEditor } from '@toasttab/sites-components'
import classnames from 'classnames'
import 'swiper/css'
import 'swiper/css/navigation'
import { Mousewheel, Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperType } from 'swiper'
import { EligibilityBadge } from './EligibilityBadge'
import { OfferCardDetails } from './OfferCardDetails'

import {
  BoostFundedGuestOffer,
  DiningOptionBehavior,
  LoyaltyBanner,
  OfferAvailabilityStatus,
  RankedPromoOfferDiscount,
  useActiveOffersQuery,
  useGuestQuery
} from 'src/apollo/onlineOrdering'
import { reportError } from 'src/lib/js/clientError'
import { useLoyalty } from 'src/public/components/online_ordering/LoyaltyContext'
import { t } from 'src/translations'
import { useCustomer } from 'public/components/online_ordering/CustomerContextCommon'
import {
  getSegmentRestrictionI18nKeys,
  getSegmentedOfferState,
  hasTargetSegment,
  SegmentedOfferState
} from 'public/components/online_ordering/segmentedOfferUtils'
import { AuthenticationSource } from 'public/components/default_template/online_ordering/checkout/checkoutUtils'
import { PwlessAuth } from 'public/components/default_template/online_ordering/account/pwlessAuth/PwlessAuth'

import ChevronIcon from 'shared/components/common/carousel/ChevronIcon'
import {
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  useModal
} from 'shared/components/common/modal'
import { useOOClient } from 'shared/components/common/oo_client_provider/OOClientProvider'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { ScreenWidth, useIsMobile } from 'shared/js/utils/WindowContext'

import { DEFAULT_COLORS } from 'public/components/default_template/meta/StyleMeta'
import { useThemeColorScheme } from 'public/components/default_template/meta/useTheme'
import {
  FreeDeliveryIllustration,
  StickerIllustration,
  TagIllustration
} from 'public/components/default_template/offers/OffersIcons'
import {
  getRestrictedDiningOption,
  isLoyaltyBannerAvailable
} from 'public/components/default_template/online_ordering/cart/offers/OfferUtils'
import { useCart } from 'public/components/online_ordering/CartContext'
import { useOptionalFulfillment } from 'public/components/online_ordering/FulfillmentContext'
import { useOffers } from 'public/components/online_ordering/OffersContext'

import { LoyaltyCard, LoyaltyCardType } from './LoyaltyCard'
import { BoostedOffer } from './BoostedOffer'
import { ToastFundedFreeDeliveryOffer } from './ToastFundedFreeDeliveryOffer'
import { useFreeDeliveryOffer } from './useFreeDeliveryOffer'
import { useStyledCardColors } from './useStyledCardColors'
import { DiscountType } from './types'
import { useFlags } from 'src/shared/components/common/feature_flags/useFlags'

export const CopyPromoCodeButton = ({ promoCode }: { promoCode: string }) => {
  const [copiedMessageVisible, setCopiedMessageVisible] = useState(false)

  const copyPromoCode = async () => {
    await navigator.clipboard.writeText(promoCode)
    setCopiedMessageVisible(true)
    setTimeout(() => {
      setCopiedMessageVisible(false)
    }, 2000)
  }

  return (
    <div className='copyButton'>
      <button onClick={copyPromoCode} aria-label={t('offers.copy-promo-code')}>
        <CopyIcon />
      </button>
      <div
        className={classnames('copyMessage', { show: copiedMessageVisible })}
      >
        {t('offers.copied')}
      </div>
    </div>
  )
}

const Offers = () => {
  const { customer } = useCustomer()
  const { restaurant, ooPromoBanners, ooRestaurant } = useRestaurant()
  const ooClient = useOOClient()
  const { data: guest, refetch } = useGuestQuery({
    client: ooClient,
    variables: { restaurantId: ooRestaurant?.guid },
    skip: !customer
  })
  const { ooBoostOffers } = useFlags()
  const { data: activeOffersData, error: activeOffersError } =
    useActiveOffersQuery({
      client: ooClient,
      variables: { restaurantGuid: ooRestaurant?.guid ?? '' },
      ssr: false,
      skip: !ooBoostOffers || !ooRestaurant?.guid
    })

  useEffect(() => {
    if (activeOffersError) {
      reportError('Error querying active offers', activeOffersError, {
        restaurantGuid: ooRestaurant?.guid
      })
    }
  }, [activeOffersError, ooRestaurant?.guid])
  const getColorFromTheme = useThemeColorScheme()
  const { rankedPromoOfferDiscounts, offerFromUrl, loading } = useOffers()
  const toastFundedFreeDeliveryOffer = useFreeDeliveryOffer()
  // Optional so the card is simply absent in the editor, which does not always
  // provide fulfillment state.
  const selectedDiningOptionBehavior =
    useOptionalFulfillment()?.selectedDiningOptionBehavior
  const swiperRef = useRef(null)
  const isMobile = useIsMobile(ScreenWidth.EXTRA_SMALL)
  const navigationPrevRef = useRef<HTMLButtonElement>(null)
  const navigationNextRef = useRef<HTMLButtonElement>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const slidesPerView = isMobile ? 1 : 2
  const { useEditableRef } = useEditor()
  const { loadingLoyaltyFeatures, programDescription, hasLoyaltyAccount } =
    useLoyalty()
  const { editableRef } = useEditableRef<HTMLDivElement>({
    name: 'promo codes',
    displayName: 'Promo Banner',
    actions: [],
    path: 'config.ooConfig.promoCodeConfig',
    schema: { fields: [] }
  })
  const promoCodeConfig = restaurant.ooConfig?.promoCodeConfig
  const deliveryBanner = ooPromoBanners?.deliveryBanners?.[0]
  const loyaltyBanner = ooPromoBanners?.loyaltyBanners?.[0]
  // If an offer came in encoded in the url, we'll want to list that one first and remove it from the rest of the list.
  const offerBanners = useMemo(
    () =>
      offerFromUrl
        ? rankedPromoOfferDiscounts?.filter(
            (offer) => offer.promoCode !== offerFromUrl.promoCode
          ) ?? []
        : rankedPromoOfferDiscounts,
    [offerFromUrl, rankedPromoOfferDiscounts]
  )

  const savedBoostOffers = useMemo(
    () =>
      new Set(
        guest?.guest?.savedOffers?.items?.map(
          (offer) => (offer as BoostFundedGuestOffer).id
        )
      ),
    [guest]
  )
  const boostOffers = useMemo(() => {
    const activeOffers =
      activeOffersData?.restaurantV2?.__typename === 'Restaurant'
        ? activeOffersData.restaurantV2.activeOffers
        : []

    return activeOffers.filter(
      (offer): offer is BoostFundedGuestOffer =>
        offer.__typename === 'BoostFundedGuestOffer' &&
        !savedBoostOffers.has(offer.id) &&
        offer.guestUsabilityStatus === 'USABLE' &&
        offer.availability?.availabilityCalculations?.currentAvailabilityStatus
          ?.status === OfferAvailabilityStatus.Available
    )
  }, [savedBoostOffers, activeOffersData])

  const [illustrationColor, arrowColor] = useMemo(() => {
    return getColorFromTheme(
      (theme) => [
        theme.colorOverrides?.offerColorOverrides?.illustration ??
          theme.colorScheme.background.primary,
        theme.colorScheme.icon.default
      ],
      [
        promoCodeConfig?.backgroundColor ??
          restaurant.meta.primaryColor ??
          DEFAULT_COLORS.primary,
        '#252525'
      ]
    )
  }, [
    getColorFromTheme,
    promoCodeConfig?.backgroundColor,
    restaurant.meta.primaryColor
  ])

  const offerSlides = useMemo(() => {
    const toastFundedFreeDeliverySlide =
      toastFundedFreeDeliveryOffer &&
      selectedDiningOptionBehavior === DiningOptionBehavior.Delivery
        ? [
            <SwiperSlide
              key={toastFundedFreeDeliveryOffer.guid}
              data-testid='swiper-slide'
            >
              <ToastFundedFreeDeliveryOffer
                offer={toastFundedFreeDeliveryOffer}
              />
            </SwiperSlide>
          ]
        : []

    const offerFromUrlSlide = offerFromUrl
      ? [
          <SwiperSlide key={offerFromUrl.bannerGuid} data-testid='swiper-slide'>
            <Offer
              offer={offerFromUrl}
              illustrationType='sticker'
              illustrationColor={illustrationColor}
              isTargetOffer
            />
          </SwiperSlide>
        ]
      : []

    const freeDeliverySlide = deliveryBanner
      ? [
          <SwiperSlide
            key={deliveryBanner.bannerGuid}
            data-testid='swiper-slide'
          >
            <Offer
              offer={deliveryBanner as RankedPromoOfferDiscount}
              illustrationType='freeDelivery'
              illustrationColor={illustrationColor}
              isFreeDeliveryOffer
            />
          </SwiperSlide>
        ]
      : []

    // Gets the timezone of the restaurant. If the restaurant is not found, defaults to 'America/New_York'.
    const timezoneId = ooRestaurant?.timeZoneId ?? 'America/New_York'

    // Only show the accrual bonus slide if the loyalty banner is also present and the user has a loyalty account
    const loyaltyAccrualBonusSlide =
      loyaltyBanner &&
      hasLoyaltyAccount &&
      isLoyaltyBannerAvailable(loyaltyBanner as LoyaltyBanner, timezoneId)
        ? [
            <SwiperSlide key='loyaltyAccrualBonus' data-testid='swiper-slide'>
              <LoyaltyCard type={LoyaltyCardType.AccrualBonus} />
            </SwiperSlide>
          ]
        : []

    const loyaltySlide =
      ooRestaurant?.loyaltyConfig?.loyaltySignupEnabled &&
      !loadingLoyaltyFeatures &&
      programDescription
        ? [
            <SwiperSlide key='loyalty' data-testid='swiper-slide'>
              <LoyaltyCard />
            </SwiperSlide>
          ]
        : []

    const promoOfferDiscountSlides =
      offerBanners?.map((offer, index) => (
        <SwiperSlide key={offer.bannerGuid} data-testid='swiper-slide'>
          <Offer
            offer={offer as RankedPromoOfferDiscount}
            illustrationType={index % 2 === 0 ? 'sticker' : 'tag'} // alternate sticker and tag icons
            illustrationColor={illustrationColor}
          />
        </SwiperSlide>
      )) ?? []

    const boostOffersSlides = boostOffers?.map(
      (offer: BoostFundedGuestOffer, index) => (
        <SwiperSlide key={offer.id} data-testid='swiper-slide'>
          <BoostedOffer
            onWalletChange={refetch}
            offer={offer}
            illustrationType={
              offer.displayText?.toLowerCase()?.includes('delivery')
                ? 'freeDelivery'
                : index % 2 === 0
                ? 'sticker'
                : 'tag'
            }
            illustrationColor={illustrationColor}
          />
        </SwiperSlide>
      )
    )

    return [
      ...toastFundedFreeDeliverySlide,
      ...offerFromUrlSlide,
      ...loyaltySlide,
      ...loyaltyAccrualBonusSlide,
      ...freeDeliverySlide,
      ...promoOfferDiscountSlides,
      ...boostOffersSlides
    ]
  }, [
    offerFromUrl,
    deliveryBanner,
    toastFundedFreeDeliveryOffer,
    selectedDiningOptionBehavior,
    hasLoyaltyAccount,
    illustrationColor,
    loadingLoyaltyFeatures,
    loyaltyBanner,
    ooRestaurant?.loyaltyConfig?.loyaltySignupEnabled,
    programDescription,
    offerBanners,
    ooRestaurant?.timeZoneId,
    boostOffers
  ])

  const rewardsSavings = t('title.rewards-and-savings')
  const sectionHeader = useMemo(
    () =>
      ooRestaurant?.loyaltyConfig?.loyaltySignupEnabled &&
      ooRestaurant.loyaltyConfig.programName
        ? rewardsSavings
        : t('offers.offers-and-savings'),
    [ooRestaurant, rewardsSavings]
  )

  if (loading || offerSlides.length === 0) {
    return null
  }

  return (
    <div className='paddedContentWrapper'>
      <div className='paddedContent'>
        <div className='offers' ref={editableRef} data-testid='offers'>
          <div className='offersHeader'>
            <h3 className='header'>{sectionHeader}</h3>
            {offerSlides.length > slidesPerView && (
              <div className='arrows'>
                <button
                  ref={navigationPrevRef}
                  aria-label={t('offers.scroll-left')}
                  className={classnames('arrow left', {
                    disabledArrow: activeIndex === 0
                  })}
                >
                  <ChevronIcon color={arrowColor} />
                </button>
                <button
                  ref={navigationNextRef}
                  aria-label={t('offers.scroll-right')}
                  className={classnames('arrow right', {
                    disabledArrow:
                      activeIndex === offerSlides.length - slidesPerView
                  })}
                >
                  <ChevronIcon color={arrowColor} />
                </button>
              </div>
            )}
          </div>
          <Swiper
            ref={swiperRef}
            className='swiperSlideContainer'
            slidesPerView={slidesPerView}
            spaceBetween={24}
            navigation={{
              prevEl: navigationPrevRef.current,
              nextEl: navigationNextRef.current
            }}
            mousewheel={offerSlides.length > slidesPerView}
            onActiveIndexChange={(swiperData: SwiperType) => {
              setActiveIndex(swiperData.activeIndex)
            }}
            modules={[Mousewheel, Navigation]}
          >
            {offerSlides}
          </Swiper>
        </div>
      </div>
    </div>
  )
}

const Offer = ({
  offer,
  illustrationType,
  illustrationColor,
  isFreeDeliveryOffer,
  isTargetOffer
}: {
  offer: RankedPromoOfferDiscount
  illustrationType: 'sticker' | 'tag' | 'freeDelivery'
  illustrationColor: string
  isFreeDeliveryOffer?: boolean
  isTargetOffer?: boolean
}) => {
  const { diningOptions } = useRestaurant()
  const { backgroundColor, textColor, borderColor, templateOverrides } =
    useStyledCardColors()
  const { cart } = useCart()
  const { isOpen, onClose, onOpen } = useModal()
  const {
    isOpen: isAuthModalOpen,
    onClose: onAuthModalClose,
    onOpen: onAuthModalOpen
  } = useModal()
  const { customer } = useCustomer()
  const appliedDiscountCode =
    cart?.order?.discounts?.restaurantDiscount?.promoCode
  const [showSplashScreen, setShowSplashScreen] = useState(true)

  const isSegmentedOffer = hasTargetSegment(offer)
  const segmentRestrictionI18nKeys = getSegmentRestrictionI18nKeys(offer)
  const segmentState: SegmentedOfferState = getSegmentedOfferState(
    offer,
    !!customer
  )

  useEffect(() => {
    setShowSplashScreen(isTargetOffer || segmentState === 'eligible')
  }, [isTargetOffer, segmentState])

  const discountType =
    offer.discount?.amountType === 'FIXED' ||
    offer.discount?.bogoAction?.amountType === 'FIXED'
      ? DiscountType.DOLLAR
      : DiscountType.PERCENT

  const illustration =
    illustrationType === 'freeDelivery' ? (
      <FreeDeliveryIllustration color={illustrationColor} />
    ) : illustrationType === 'tag' ? (
      <TagIllustration color={illustrationColor} discountType={discountType} />
    ) : (
      <StickerIllustration
        color={illustrationColor}
        discountType={discountType}
      />
    )

  const copyButton = <CopyPromoCodeButton promoCode={offer.promoCode} />

  const isSingleUseOffer =
    offer.discount?.promoCodes &&
    offer.discount.promoCodes.length > 0 &&
    offer.discount.promoCodes[0]?.singleUse
  const restrictedDiningOption = getRestrictedDiningOption(
    offer.discount?.triggerDiningOptions,
    diningOptions
  )

  const shimmerStyles = {
    background: `linear-gradient(-45deg, ${illustrationColor}0F 40%, ${backgroundColor} 50%, ${illustrationColor}0F 60%)`,
    backgroundSize: '300%',
    backgroundPositionX: '100%',
    animation: 'shimmer 2.5s infinite ease-in-out'
  }

  const shouldShowSegmentedOfferCTA =
    segmentState === 'logged-out' || segmentState === 'eligible'
  const segmentedOfferCTALabel =
    segmentState === 'logged-out'
      ? t('offers.sign-up-to-redeem')
      : t('offers.continue-to-order')
  const segmentedOfferCTAClickHandler = () => {
    if (segmentState === 'logged-out') {
      onClose()
      onAuthModalOpen()
    }
    if (segmentState === 'eligible') {
      onClose()
    }
  }

  const offerRestrictions = useMemo(() => {
    if (
      isSegmentedOffer &&
      segmentRestrictionI18nKeys?.restrictionsWhenIneligible &&
      segmentState === 'ineligible'
    ) {
      return t(segmentRestrictionI18nKeys.restrictionsWhenIneligible)
    } else if (isSegmentedOffer && segmentRestrictionI18nKeys?.restrictions) {
      return t(segmentRestrictionI18nKeys.restrictions)
    } else if (isSingleUseOffer) {
      return t('offers.single-use-offer-description')
    }
    return null
  }, [
    isSegmentedOffer,
    segmentRestrictionI18nKeys,
    segmentState,
    isSingleUseOffer
  ])

  return (
    <>
      <button
        className={classnames(
          'styledCard',
          templateOverrides.borderStroke,
          templateOverrides?.roundedCorner,
          templateOverrides?.dropShadow
        )}
        style={{
          borderColor,
          color: textColor,
          background: `linear-gradient(270deg, ${illustrationColor}22 5%, ${backgroundColor} 25%)`,
          // eslint-disable-next-line no-extra-parens
          ...((isTargetOffer || segmentState === 'eligible') &&
          !showSplashScreen
            ? shimmerStyles
            : {}),
          ...(segmentState === 'ineligible' ? { filter: 'grayscale(1)' } : {})
        }}
        onClick={onOpen}
        data-testid={`offer-${offer.bannerGuid}`}
      >
        {showSplashScreen ? (
          <div
            className='offerSplashScreen'
            onAnimationEnd={() => setShowSplashScreen(false)}
          >
            <div className='illustration'>{illustration}</div>
            <div
              className='cardText'
              data-testid='offer-text'
              style={{ color: illustrationColor }}
            >
              <div className='offerUnlocked'>{t('offers.offer-unlocked')}</div>
              <div className='offerText'>{offer.bannerText}</div>
            </div>
          </div>
        ) : (
          <div
            className={classnames(
              'cardContent',
              isTargetOffer || segmentState === 'eligible'
                ? 'withAnimation'
                : undefined
            )}
          >
            <div className='cardInfo'>
              <div className='cardTitleGroup'>
                <div className='cardText' data-testid='offer-text'>
                  {offer.bannerText}
                </div>
                {isSegmentedOffer &&
                  segmentRestrictionI18nKeys?.restrictions && (
                    <div className='segmentedOfferRestriction'>
                      {t(segmentRestrictionI18nKeys.restrictions)}
                    </div>
                  )}
              </div>
              <OfferCardDetails
                isSegmentedOffer={isSegmentedOffer}
                segmentState={segmentState}
                isFreeDeliveryOffer={isFreeDeliveryOffer}
                isApplied={appliedDiscountCode === offer.promoCode}
              />
            </div>
            <div
              className='illustration'
              data-testid={`offer-illustration-${illustrationType}-${discountType}`}
            >
              {illustration}
            </div>
            {(isTargetOffer || segmentState === 'eligible') && (
              <div
                className='offerUnlocked'
                style={{
                  backgroundColor: illustrationColor,
                  color: backgroundColor
                }}
              >
                {t('offers.offer-unlocked')}
              </div>
            )}
          </div>
        )}
      </button>
      <Modal isOpen={isOpen} onClose={onClose} className='offerModalWrapper'>
        <ModalOverlay fadeIn fadeOut />
        <ModalContent contentClassName='offerModal'>
          <ModalCloseButton />
          <div className='content' data-testid='offer-modal'>
            <div
              className='header'
              style={{
                background: `linear-gradient(0deg, white, ${illustrationColor}22)`,
                ...(segmentState === 'ineligible'
                  ? { filter: 'grayscale(1)' }
                  : {})
              }}
            >
              {illustration}
              {isSegmentedOffer && (
                <EligibilityBadge segmentState={segmentState} />
              )}
              <div className='title'>{offer.bannerText}</div>
              {offer.offerDescription && (
                <div className='description'>{offer.offerDescription}</div>
              )}
              {restrictedDiningOption && (
                <div className='description'>
                  {t('offers.offer-valid-for', {
                    diningOption:
                      restrictedDiningOption === DiningOptionBehavior.TakeOut
                        ? t('offers.pickup')
                        : t('offers.delivery')
                  })}
                </div>
              )}
              {!isSegmentedOffer && (
                <div>
                  {isFreeDeliveryOffer
                    ? t('offers.automatically-applied')
                    : t('offers.apply-at-checkout')}
                </div>
              )}
            </div>
            <div className='body'>
              {offerRestrictions && (
                <OfferDetail
                  title={t('offers.restrictions')}
                  description={offerRestrictions}
                />
              )}
              {!isSegmentedOffer && offer.promoCode && (
                <OfferDetail
                  title={t('offers.promo-code')}
                  description={offer.promoCode}
                  action={copyButton}
                />
              )}
            </div>
            {shouldShowSegmentedOfferCTA && (
              <div className='segmentedOfferCtaWrapper'>
                <button
                  className='segmentedOfferCta primaryCta'
                  onClick={segmentedOfferCTAClickHandler}
                >
                  {segmentedOfferCTALabel}
                </button>
              </div>
            )}
          </div>
        </ModalContent>
      </Modal>
      <Modal
        isOpen={isAuthModalOpen}
        onClose={onAuthModalClose}
        className='authModalWrapper'
      >
        <ModalOverlay fadeIn fadeOut />
        <PwlessAuth
          source={AuthenticationSource.SegmentedOfferCard}
          onSuccessfulLogin={onAuthModalClose}
        />
      </Modal>
    </>
  )
}

const OfferDetail = ({
  title,
  description,
  action
}: {
  title: string
  description: string
  action?: ReactNode
}) => {
  return (
    <div className='offerDetail'>
      <div className='detail'>
        <div className='detailTitle'>{title}</div>
        <div className='detailDescription'>{description}</div>
      </div>
      {action ? action : null}
    </div>
  )
}

export default Offers
